import { useCallback, useEffect, useMemo, useState } from 'react';
import { makeGetCall } from '../../../utils/helper';
import { REPORT_CONFIG } from '../constants';
import type { TemplateNode, ReportTemplate } from '../types';

/**
 * Manages template node + template selection for the report editor.
 * Handles:
 *  - Fetching nodes for a given modality
 *  - Auto-selecting a node from the order's body part
 *  - Fetching templates for the selected node
 *  - Auto-selecting the first template when the editor is empty
 */
export const useTemplates = (modality: string | undefined, bodyPart: string | undefined) => {
  const [nodes, setNodes] = useState<TemplateNode[]>([]);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  // ── Fetch nodes on modality change ──
  useEffect(() => {
    if (!modality) return;
    makeGetCall(`/get-nodes?modality=${modality}`)
      .then(res => setNodes(res?.data?.data || []))
      .catch(() => setNodes([]));
  }, [modality]);

  // ── Auto-select node from body part ──
  useEffect(() => {
    if (!REPORT_CONFIG.AUTO_SELECT_NODE_FROM_BODYPART) return;
    if (nodes.length > 0 && bodyPart && !selectedNode) {
      const match = nodes.find(n => n.code === bodyPart);
      if (match) setSelectedNode(match.code);
    }
  }, [nodes, bodyPart, selectedNode]);

  // ── Fetch templates when node changes ──
  useEffect(() => {
    if (!selectedNode || !modality) {
      setTemplates([]);
      return;
    }
    makeGetCall(`/get-templates?modality=${modality}&node=${selectedNode}`)
      .then(res => setTemplates(res?.data?.data || []))
      .catch(() => setTemplates([]));
  }, [selectedNode, modality]);

  const handleNodeChange = useCallback((val: string | null) => {
    setSelectedNode(val);
    setTemplates([]);
    setSelectedTemplate(null);
  }, []);

  const handleTemplateSelect = useCallback((val: string | null, templateHtml?: string) => {
    setSelectedTemplate(val);
    // The caller is responsible for applying template HTML to the editor
  }, []);

  /** Reset selections (e.g. when switching to a different order) */
  const resetSelections = useCallback(() => {
    setSelectedNode(null);
    setSelectedTemplate(null);
    setTemplates([]);
  }, []);

  const nodeOptions = useMemo(() =>
    nodes.map(n => ({ label: n.label, value: n.code })),
  [nodes]);

  const templateOptions = useMemo(() =>
    templates.map(t => ({
      label: t.rt_display_name,
      value: String(t.rt_id),
      key: String(t.rt_id),
      templateHtml: t.template_html || '<div></div>',
    })),
  [templates]);

  return {
    nodes,
    templates,
    selectedNode,
    selectedTemplate,
    setSelectedNode,
    setSelectedTemplate,
    handleNodeChange,
    handleTemplateSelect,
    resetSelections,
    nodeOptions,
    templateOptions,
  };
};
